<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { api, jsonBody } from '../../services/api'

interface Quote {
  id: string
  name: string
  text: string
  isActive: boolean
  bottleCount: number
  hiddenCount: number
  findCount: number
  commentCount: number
}
const items = ref<Quote[]>([])
const search = ref('')
const showCreate = ref(false)
const form = ref({ name: '', text: '', description: '', isActive: true })
const error = ref('')

async function load() {
  const result = await api<{ items: Quote[] }>(
    `/admin/quotes?search=${encodeURIComponent(search.value)}`,
  )
  items.value = result.items
}
async function createQuote() {
  try {
    await api('/admin/quotes', { method: 'POST', ...jsonBody(form.value) })
    form.value = { name: '', text: '', description: '', isActive: true }
    showCreate.value = false
    await load()
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : 'Quote could not be created.'
  }
}
async function duplicate(id: string) {
  await api(`/admin/quotes/${id}/duplicate`, { method: 'POST' })
  await load()
}
async function archive(id: string) {
  await api(`/admin/quotes/${id}/archive`, { method: 'POST' })
  await load()
}
onMounted(load)
</script>

<template>
  <header class="admin-heading">
    <div>
      <p class="eyebrow">Reusable messages</p>
      <h1>Quotes</h1>
      <p>Create each physical message once, then follow every bottle carrying it.</p>
    </div>
    <button class="button primary" @click="showCreate = !showCreate">+ New quote</button>
  </header>
  <p v-if="error" class="form-error banner">{{ error }}</p>
  <form v-if="showCreate" class="admin-card editor-form" @submit.prevent="createQuote">
    <div class="card-heading">
      <h2>Create quote</h2>
      <button class="icon-button" type="button" @click="showCreate = false">×</button>
    </div>
    <div class="form-grid">
      <label>Internal name<input v-model="form.name" required maxlength="160" /></label
      ><label class="span-2"
        >Quote text<textarea v-model="form.text" required maxlength="2000" rows="3" /></label
      ><label class="span-2"
        >Description <span class="optional">optional</span
        ><textarea v-model="form.description" maxlength="4000" rows="2" />
      </label>
    </div>
    <button class="button primary">Create quote</button>
  </form>
  <section class="admin-card table-card">
    <div class="table-tools">
      <label class="search-field"
        ><span>⌕</span><input v-model="search" placeholder="Search quotes" @input="load" /></label
      ><span>{{ items.length }} shown</span>
    </div>
    <div class="data-table">
      <table>
        <thead>
          <tr>
            <th>Quote</th>
            <th>Bottles</th>
            <th>Hidden</th>
            <th>Finds</th>
            <th>Messages</th>
            <th>Status</th>
            <th><span class="sr-only">Actions</span></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="quote in items" :key="quote.id">
            <td>
              <RouterLink :to="`/admin/quotes/${quote.id}`"
                ><strong>{{ quote.name }}</strong
                ><small>{{ quote.text }}</small></RouterLink
              >
            </td>
            <td>{{ quote.bottleCount }}</td>
            <td>{{ quote.hiddenCount }}</td>
            <td>{{ quote.findCount }}</td>
            <td>{{ quote.commentCount }}</td>
            <td>
              <span class="status-pill" :class="quote.isActive ? 'active' : 'retired'">{{
                quote.isActive ? 'Active' : 'Archived'
              }}</span>
            </td>
            <td>
              <div class="row-actions">
                <button @click="duplicate(quote.id)">Duplicate</button
                ><button v-if="quote.isActive" @click="archive(quote.id)">Archive</button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>
